import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

let app = null;
let messaging = null;

export function getFirebaseApp() {
  if (!app) {
    app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  }
  return app;
}

export function getFirebaseMessaging() {
  if (!messaging) {
    const fbApp = getFirebaseApp();
    messaging = getMessaging(fbApp);
  }
  return messaging;
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications');
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  return permission;
}

/**
 * Wait for a service worker registration to reach the "activated" state.
 */
function waitForActivation(registration) {
  return new Promise((resolve) => {
    const sw = registration.installing || registration.waiting || registration.active;
    if (!sw) { resolve(registration); return; }
    if (sw.state === 'activated') { resolve(registration); return; }
    sw.addEventListener('statechange', function onStateChange() {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', onStateChange);
        resolve(registration);
      }
    });
    // Safety timeout - don't wait forever
    setTimeout(() => resolve(registration), 5000);
  });
}

export async function getFCMToken() {
  await requestNotificationPermission();
  const msg = getFirebaseMessaging();
  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  
  let swRegistration = null;
  if ('serviceWorker' in navigator) {
    try {
      // Register with updateViaCache: 'none' to bypass HTTP cache
      swRegistration = await navigator.serviceWorker.register(
        '/firebase-messaging-sw.js',
        { updateViaCache: 'none' }
      );
      console.log('[FCM] SW registered, scope:', swRegistration.scope);
      
      // Force check for updated SW file
      await swRegistration.update();
      console.log('[FCM] SW update check done');
      
      // Wait for the SW to be active (handles skipWaiting/claim cycle)
      swRegistration = await waitForActivation(swRegistration);
      console.log('[FCM] SW active:', swRegistration.active?.state);
    } catch (swErr) {
      console.error('[FCM] SW registration failed:', swErr);
    }
  }
  
  const tokenOptions = { vapidKey };
  if (swRegistration) {
    tokenOptions.serviceWorkerRegistration = swRegistration;
  }
  
  const token = await getToken(msg, tokenOptions);
  console.log('[FCM] Token obtained:', token ? `${token.substring(0, 20)}... (len=${token.length})` : 'null');
  return token;
}

export function onForegroundMessage(callback) {
  const msg = getFirebaseMessaging();
  return onMessage(msg, (payload) => {
    console.log('[FCM] Foreground message:', payload);
    callback(payload);
  });
}
